import { useEffect, useState } from "react";
import { KeyRound, Check, Loader2, Trash2, ExternalLink, Crown, Sparkles } from "lucide-react";
import { getOpenAiKey, setOpenAiKey, deleteOpenAiKey, startCheckout } from "../lib/api";
import { toast } from "sonner";

export function AiKeyPanel() {
  const [status, setStatus] = useState(null);
  const [key, setKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  const load = () => getOpenAiKey().then(setStatus).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!key.trim()) return;
    setSaving(true);
    try {
      const r = await setOpenAiKey(key.trim());
      toast.success(`Key connected (${r.masked}). Your uploads now bill your own OpenAI account.`);
      setKey("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't verify that key"); }
    finally { setSaving(false); }
  };

  const remove = async () => { await deleteOpenAiKey(); toast.success("Key removed"); load(); };

  const upgrade = async () => {
    try {
      const r = await startCheckout(window.location.origin, "platform_monthly");
      if (r.checkout_url) window.location.href = r.checkout_url;
    } catch { toast.error("Couldn't start checkout"); }
  };

  if (!status) return null;
  const trialLeft = Math.max(0, (status.trial_limit || 0) - (status.trial_shows_used || 0));

  return (
    <div className="border border-border bg-card" data-testid="ai-key-panel">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between px-5 py-4 text-left">
        <span className="flex items-center gap-2 font-body text-sm font-semibold">
          <KeyRound className="h-4 w-4 text-accent" /> Your OpenAI key &amp; billing
        </span>
        <span className={`font-mono text-[10px] uppercase tracking-wider ${status.has_key ? "text-accent" : "text-primary"}`}>
          {status.has_key ? `Connected · ${status.masked}` : trialLeft > 0 ? `${trialLeft} free trial show left` : "Trial used — add a key"}
        </span>
      </button>

      {open && (
        <div className="border-t border-border px-5 py-5">
          <p className="font-body text-sm text-muted-foreground">
            You pay a flat <span className="text-white">$19/mo</span> for the platform. The AI cost of processing your shows
            (~$3–4 per 5-hour show) is billed to <span className="text-white">your own OpenAI account</span> — so your costs
            stay low and predictable no matter how much you upload.
          </p>

          {status.has_key ? (
            <div className="mt-4 flex items-center gap-3">
              <span className="inline-flex items-center gap-2 border border-accent/50 px-3 py-1.5 font-mono text-xs text-accent"><Check className="h-3.5 w-3.5" /> {status.masked}</span>
              <button data-testid="remove-key-btn" onClick={remove} className="inline-flex items-center gap-1 font-mono text-xs text-muted-foreground hover:text-primary"><Trash2 className="h-3.5 w-3.5" /> Remove</button>
            </div>
          ) : (
            <>
              <ol className="mt-4 list-inside list-decimal space-y-1 font-body text-xs text-muted-foreground">
                <li>Go to <a href="https://platform.openai.com/api-keys" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-accent hover:underline">platform.openai.com/api-keys <ExternalLink className="h-3 w-3" /></a> (free to create).</li>
                <li>Add a payment method under Billing, then click <span className="text-white">Create new secret key</span>.</li>
                <li>Copy it (starts with <span className="font-mono">sk-</span>) and paste it below.</li>
              </ol>
              <div className="mt-3 flex flex-wrap gap-2">
                <input data-testid="openai-key-input" value={key} onChange={(e) => setKey(e.target.value)} type="password"
                       placeholder="sk-..." className="min-w-[240px] flex-1 rounded-none border border-border bg-background px-3 py-2 font-mono text-sm text-white outline-none focus:border-accent" />
                <button data-testid="save-key-btn" onClick={save} disabled={saving}
                        className="inline-flex items-center gap-2 bg-accent px-4 py-2 font-body text-xs font-semibold text-black disabled:opacity-50">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Connect key
                </button>
              </div>
              {trialLeft > 0 && (
                <p className="mt-3 inline-flex items-center gap-1 font-body text-xs text-primary"><Sparkles className="h-3.5 w-3.5" /> You have {trialLeft} free trial show on us — upload now to try it, no key needed.</p>
              )}
            </>
          )}

          <div className="mt-5 border-t border-border pt-4">
            <button data-testid="upgrade-platform-btn" onClick={upgrade}
                    className="inline-flex items-center gap-2 border border-primary px-4 py-2 font-body text-xs font-semibold text-primary transition-colors hover:bg-primary hover:text-white">
              <Crown className="h-4 w-4" /> Subscribe — $19/mo platform plan
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
